import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useInView } from '../../hooks/useInView';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goglobalworld.net';
  
  useSEO({
    title: 'Get Free Quote | Wholesale Construction Materials Supplier | Save 20-40% on Building Costs',
    description: 'Request a free quote for direct import building materials. Wholesale construction materials supplier serving custom home builders, developers, and contractors across Canada. Get factory-direct pricing on steel building packages, bulk construction materials, kitchens, windows, flooring. Compare costs and save 20-40% on your construction project. Fast response within 24-48 hours.',
    keywords: 'construction materials quote, wholesale building supplies, direct import materials pricing, bulk construction materials, steel building supplier, save on building materials, factory direct pricing',
    canonical: `${siteUrl}/contact`,
    ogImage: 'https://public.readdy.ai/ai/img_res/40d0d072af0f1d07c841941f5a188208.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Go Global Inc.',
      description: 'Get free quote for wholesale construction materials and direct import building supplies',
      mainEntity: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'info@goglobalinc.com',
          contactType: 'Sales',
          availableLanguage: 'English',
          areaServed: 'CA'
        }
      }
    },
  });

  const [ref, isInView] = useInView({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    projectType: '',
    projectScale: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('projectType', formData.projectType);
      formBody.append('projectScale', formData.projectScale);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5m5h6944v703eh9117g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          company: '',
          email: '',
          projectType: '',
          projectScale: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-display font-light text-5xl md:text-6xl text-gray-900 leading-tight mb-6">
                Start the <span className="font-semibold">Conversation</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Tell us about your project and we'll show you how Go Global Inc. can deliver 
                better outcomes for your construction supply needs.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section ref={ref} className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Left - Trust Indicators */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="lg:col-span-1 space-y-8"
              >
                <div>
                  <h3 className="font-display font-semibold text-lg text-gray-900 mb-3">
                    What to Expect
                  </h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-primary-600 mr-2 mt-0.5"></i>
                      <span>Response within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-primary-600 mr-2 mt-0.5"></i>
                      <span>Confidential consultation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-primary-600 mr-2 mt-0.5"></i>
                      <span>No obligation pricing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-primary-600 mr-2 mt-0.5"></i>
                      <span>Dedicated account manager</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
                  <i className="ri-shield-check-line text-3xl mb-3"></i>
                  <h3 className="font-semibold text-base mb-2">Your Privacy Matters</h3>
                  <p className="text-sm text-white/90">
                    All information shared is kept strictly confidential and used only to serve your project needs.
                  </p>
                </div>
              </motion.div>

              {/* Right - Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2"
              >
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-900 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
                        Company *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Your Company Name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-gray-900 mb-2">
                        Project Type *
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                      >
                        <option value="">Select project type</option>
                        <option value="Real Estate Development">Real Estate Development</option>
                        <option value="General Contracting">General Contracting</option>
                        <option value="Hospitality">Hospitality</option>
                        <option value="Industrial">Industrial</option>
                        <option value="Mixed-Use">Mixed-Use</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="projectScale" className="block text-sm font-medium text-gray-900 mb-2">
                        Project Scale *
                      </label>
                      <select
                        id="projectScale"
                        name="projectScale"
                        value={formData.projectScale}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                      >
                        <option value="">Select project scale</option>
                        <option value="Under $500K">Under $500K</option>
                        <option value="$500K - $1M">$500K - $1M</option>
                        <option value="$1M - $5M">$1M - $5M</option>
                        <option value="$5M - $10M">$5M - $10M</option>
                        <option value="$10M - $50M">$10M - $50M</option>
                        <option value="Over $50M">Over $50M</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="Tell us about your project requirements, timeline, and any specific materials you need..."
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-base font-semibold rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <i className="ri-arrow-right-line ml-2"></i>}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                      <i className="ri-check-line mr-2"></i>
                      Thank you! We'll be in touch within 24 hours.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                      <i className="ri-error-warning-line mr-2"></i>
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;